// SD 卡片段清單 + 儲存到手機（spec 17 §3.4「SD 卡片段」段落、§5.3；業主 2026-09-09 裁決納入）。
//
// 鏡像 Android `Playback.kt` 的 `sdRangeChunks` / `sdClipRows` 與
// `SdcardPlaybackController.fetchWindowTs`，讓三平台的 SD 片段列與下載腿同一套語意：
//   - 清單資料源 = `PlaybackQuery day-ranges`（continuous_only）切成 ≤5 分鐘 chunk
//     （與 §5.3 連續軸續播同一單位）+ `range-events` 的事件片段（標「含事件」）。
//   - 下載 = 對該窗取 device VOD `/pb.m3u8?t1&t2`，逐段經 PB/1 tunnel 拉 `/seg/*.ts`
//     接續成**單一 TS**，呼叫端再走既有 transmux 腿（tsToMp4）出 MP4。
//
// 本檔只放純函式 + 以注入的 `get` 抓資料的組裝函式（不碰 React / signaling 單例），
// 方便 vitest 直接驗算法。

import type { PbEvent, PbRange } from './sdcardQuery';
import { pbPlaylistUrl } from './hlsSource';

/** 單一 SD 片段列的長度上限（5 分鐘，µs）— 同 §5.3 `REC_PLAYBACK_MAX_US` / Android `SD_CHUNK_US`。 */
export const SD_CHUNK_US = 300_000_000;
/** 事件片段窗：事件前 5 秒 / 後 10 秒（spec 17 §5.3 `EVENT_PRE_US` / `EVENT_POST_US`，硬值）。 */
export const SD_EVENT_PRE_US = 5_000_000;
export const SD_EVENT_POST_US = 10_000_000;

/** 「SD 卡」清單的一列（對齊 Android `RecordingClip(clipId="sd-…")`）。 */
export interface SdClip {
  /** `sd-<start>-<end>`；穩定 key，也讓呼叫端一眼分辨 SD 列。 */
  id: string;
  startUs: number;
  endUs: number;
  /** true = 事件片段（顯示「含事件」）；連續錄影 chunk 為 false。 */
  hasEvent: boolean;
}

/** 事件片段窗 [event_ts − 5s, event_ts + 10s]（§5.3；與點播同窗）。 */
export function sdEventWindow(eventTsUs: number): SdClip {
  const startUs = eventTsUs - SD_EVENT_PRE_US;
  const endUs = eventTsUs + SD_EVENT_POST_US;
  return { id: `sd-${startUs}-${endUs}`, startUs, endUs, hasEvent: true };
}

/**
 * day-ranges（連續錄影區段）→ ≤5 分鐘 chunk 列。每個區段從起點切，最後一塊吃到區段尾；
 * 空/反向區段不出列。鏡像 Android `sdRangeChunks`。
 */
export function sdRangeChunks(ranges: PbRange[]): SdClip[] {
  const out: SdClip[] = [];
  for (const r of ranges) {
    let s = r.start_us;
    while (s < r.end_us) {
      const e = Math.min(s + SD_CHUNK_US, r.end_us);
      out.push({ id: `sd-${s}-${e}`, startUs: s, endUs: e, hasEvent: false });
      s = e;
    }
  }
  return out;
}

/**
 * 「SD 卡」清單 = 連續錄影 chunk（hasEvent=false）+ 事件片段（hasEvent=true）。
 * 落在某個連續區段內的事件片段不重列（那段影像已在 chunk 裡）；同一起點的事件片段去重；
 * 空窗（end ≤ start）略過。依起點升冪回傳（呼叫端要「最新在上」自行反轉）。鏡像 Android `sdClipRows`。
 */
export function sdClipRows(continuous: PbRange[], events: PbRange[]): SdClip[] {
  const rows = sdRangeChunks(continuous);
  const seen = new Set<number>();
  const sorted = [...events].sort((a, b) => a.start_us - b.start_us);
  for (const ev of sorted) {
    if (ev.end_us <= ev.start_us || seen.has(ev.start_us)) continue;
    seen.add(ev.start_us);
    if (continuous.some((c) => ev.start_us >= c.start_us && ev.end_us <= c.end_us)) continue;
    rows.push({ id: `sd-${ev.start_us}-${ev.end_us}`, startUs: ev.start_us, endUs: ev.end_us, hasEvent: true });
  }
  return rows.sort((a, b) => a.startUs - b.startUs);
}

/** range-events 的 marker → 事件片段窗（PbRange 形狀，餵 sdClipRows）。 */
export function pbEventRanges(events: PbEvent[]): PbRange[] {
  return events.map((ev) => ({
    start_us: ev.clip_start_us || ev.event_ts_us - SD_EVENT_PRE_US,
    end_us: ev.clip_end_us || ev.event_ts_us + SD_EVENT_POST_US,
  }));
}

/**
 * 解析 device VOD playlist：非 `#` 開頭的非空行就是段路徑。相對路徑補成 `/…`（裝置照
 * `/seg/<seq>.ts` 列段），絕對 http(s) URL 原樣保留（同 Android `fetchWindowTs` 的正規化）。
 */
export function parseM3u8SegmentPaths(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    if (/^https?:\/\//i.test(line) || line.startsWith('/')) out.push(line);
    else out.push(`/${line}`);
  }
  return out;
}

/** 抓 tunnel / LAN 資源的最小介面（PbTunnel.requestUrl 直接相容）。 */
export type SdWindowGet = (url: string) => Promise<{ status: 'ok' | 'not_found' | 'error'; text?: string; bytes?: Uint8Array }>;

export interface SdWindowTs {
  /** 接續後的單一 MPEG-TS；`written === 0` 時為空陣列。 */
  ts: Uint8Array;
  /** 實際寫入的段數；0 = 這段沒有可用片段（playlist 空或全 404 = 已被覆蓋，不算失敗）。 */
  written: number;
  /** playlist 列出的段數（含 404 被跳過的）。 */
  total: number;
}

/**
 * SD 片段「儲存到手機」的抓取腿（spec 17 §3.4 SD 段落；鏡像 Android `fetchWindowTs`）：
 * 取 `/pb.m3u8?t1&t2` → 逐段抓 → 接續成一個 TS。
 * - playlist 非 200 → 擲錯（裝置在線但拒答，屬失敗）。
 * - 段 200 → 接續；**404 → 跳過**（event 錄影時間軸有洞、或環形已覆蓋，裝置照連續軸列段）；
 *   其他狀態 → 擲錯。
 * - 全部 404 / playlist 空 → `written = 0`，交呼叫端顯示「此時段的 SD 卡片段已被覆蓋」、不算失敗。
 */
export async function fetchSdWindowTs(get: SdWindowGet, startUs: number, endUs: number): Promise<SdWindowTs> {
  const pl = await get(pbPlaylistUrl('', startUs, endUs));
  if (pl.status !== 'ok') throw new Error(`pb.m3u8 status ${pl.status}`);
  const text = pl.text ?? (pl.bytes ? new TextDecoder().decode(pl.bytes) : '');
  const paths = parseM3u8SegmentPaths(text);
  const parts: Uint8Array[] = [];
  let total = 0;
  for (const p of paths) {
    const seg = await get(p);
    if (seg.status === 'ok') {
      if (seg.bytes && seg.bytes.length > 0) {
        parts.push(seg.bytes);
        total += seg.bytes.length;
      }
      continue;
    }
    if (seg.status === 'not_found') continue;
    throw new Error(`seg ${p} status ${seg.status}`);
  }
  const ts = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    ts.set(p, off);
    off += p.length;
  }
  return { ts, written: parts.length, total: paths.length };
}

/** 下載檔名 `{deviceId}_{yyyyMMdd}_{HHmmss}.mp4`（本地時區；與雲端 clip / iOS / Android 同規則）。 */
export function clipDownloadFileName(deviceId: string, startUs: number): string {
  const d = new Date(startUs / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  const safeId = deviceId.replace(/[^A-Za-z0-9._-]/g, '_');
  return `${safeId}_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}.mp4`;
}
